import ReactDOM from "react-dom";
import { ThemeProvider } from "@fluentui/react";
import { QueryClient, QueryClientProvider } from "react-query";
import { MsalProvider } from "@azure/msal-react";

import App from "./App";
import { DataConfigProvider } from "components/state/DataConfigProvider";
import { getMsalApplication } from "utils/helpers/auth";
import { AuthPage } from "components/auth";
import { AUTH_ENABLED } from "utils/helpers/authConstants";
import {
  EXPLORER_PREFIX,
  PRPV_PARAM,
  PRPV_HOST_INDICATOR,
  PRPV_HOST_REPLACEMENT,
  GEOCATALOGNAME_PARAM,
  isValidGeocatalogName,
} from "utils/constants";

import "./styles/index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
    },
  },
});

function checkRequiredParams() {
  const url = new URL(window.location.href);

  // Skip param enforcement on localhost — no geocatalogname needed for local dev.
  if (url.hostname === "localhost" || url.hostname === "127.0.0.1") {
    return true;
  }

  // Check if either "geocatalogname", "namespace", and "region" parameters are present -
  // or "state" parameter is present (for OAuth redirects)
  let fragment = url.hash.substring(1);
  let params = new URLSearchParams(fragment);
  let state = params.get("state");
  if (state) {
    return true;
  }
  // TODO: Remove this redirection once we deprecate the old explorer completely.
  // This is to ensure that users who have bookmarked the old url format or are using the old url format are
  // redirected to the correct url with geocatalogname in the query params.
  // If the current url does not begin with http://explorer.geocatalog.spatio.azure.com
  // then we would like to do a redirect to the correct url format with geocatalogname in the query params
  if (!url.hostname.startsWith("explorer.geocatalog.")) {
    // the geocatalogname parameter needs to be extracted from the original hostname which is in the format
    //  <geocatalogname>.<namespace>.<region>.<domain>
    const hostnameParts = url.hostname.split(".");
    if (hostnameParts.length >= 4) {
      const geocatalogname = hostnameParts.slice(0, 3).join(".");
      let newHostname = EXPLORER_PREFIX + "." + hostnameParts.slice(3).join(".");
      // If "prvpr" is present we do privateRP redirection
      if (url.searchParams.get(PRPV_PARAM) === "true") {
        newHostname = newHostname.replace(
          PRPV_HOST_INDICATOR,
          PRPV_HOST_REPLACEMENT
        );
      }
      const searchParams =
        "?" +
        GEOCATALOGNAME_PARAM +
        "=" +
        geocatalogname +
        (url.searchParams ? "&" + url.searchParams.toString() : "");
      // Preserve original search and hash parameters
      const newUrl = `${url.protocol}//${newHostname}${url.pathname}${searchParams}${
        url.hash ? url.hash : ""
      }`;
      window.location.replace(newUrl);
      return false;
    }
  }
  const requiredParams = [GEOCATALOGNAME_PARAM];
  const missingParams = requiredParams.filter(param => !url.searchParams.get(param));
  // make sure geocatalogname is in the correct format <geocatalogname>.<namespace>.<region>
  const geocatalogname = url.searchParams.get(GEOCATALOGNAME_PARAM);
  if (geocatalogname && !isValidGeocatalogName(geocatalogname)) {
    missingParams.push(
      `${GEOCATALOGNAME_PARAM} (expected format: <geocatalogname>.<namespace>.<region>)`
    );
  }
  if (missingParams.length > 0) {
    const errorMessage = `
      <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 100px auto; padding: 20px; border: 2px solid #d83b01; background-color: #fff4ce; border-radius: 4px;">
        <h2 style="color: #d83b01; margin-top: 0;">Missing Required URL Parameters</h2>
        <p><strong>The following required parameters are missing:</strong> ${missingParams.join(
          ", "
        )}</p>
        <p>Please include these parameters in the URL to access the application.</p>
        <p><strong>Example URL format:</strong></p>
        <code style="display: block; background: #f3f2f1; padding: 10px; margin: 10px 0; border-radius: 4px; overflow-x: auto;">
          ${window.location.origin}${
      window.location.pathname
    }?${GEOCATALOGNAME_PARAM}=value.namespace.region
        </code>
        <p style="margin-top: 20px; font-size: 14px; color: #605e5c;">
          These parameters are required to configure the application's backend connection.
        </p>
      </div>
    `;
    document.getElementById("root").innerHTML = errorMessage;
    return false;
  }
  return true;
}

async function setupApp() {
  if (!checkRequiredParams()) {
    return;
  }

  const msalInstance = await getMsalApplication();

  ReactDOM.render(
    <ThemeProvider>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <DataConfigProvider
            collectionConfig={{}}
            storageCollectionConfig={{}}
            groupConfig={{}}
            featuredIds={[]}
          >
            <AuthPage enabled={AUTH_ENABLED}>
              <App />
            </AuthPage>
          </DataConfigProvider>
        </QueryClientProvider>
      </MsalProvider>
    </ThemeProvider>,
    document.getElementById("root")
  );
}

setupApp();
